import { UrlApi } from "../../../services/apirest";

/**
 * Contrato API — Pagos no proveedor
 *
 * Base: /pagos-no-proveedor
 *
 * GET    /pagos-no-proveedor?id_empresa=&concepto=&estado=&fecha_desde=&fecha_hasta=
 * POST   /pagos-no-proveedor
 *        body: { id_empresa, monto, concepto, motivo, fecha, user }
 * PUT    /pagos-no-proveedor/:id  (solo estado pendiente)
 * POST   /pagos-no-proveedor/:id/pagar  body: { user }
 * POST   /pagos-no-proveedor/:id/anular body: { user, motivo? }
 *
 * Menú BD: ruta = "pagos_no_proveedor"
 */
export const MENU_RUTA_PAGOS_NO_PROVEEDOR = "pagos_no_proveedor";

const BASE = `${UrlApi}/pagos-no-proveedor`;

const errorFromBody = (data, fallback) =>
  data?.msg || data?.message || data?.error || fallback;

const parseJsonResponse = async (res, fallbackError) => {
  const text = await res.text();
  const trimmed = String(text || "").trim();

  if (!trimmed) {
    if (!res.ok) {
      throw new Error(
        `${fallbackError} (HTTP ${res.status}). El endpoint puede no existir aún.`
      );
    }
    return {};
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    const pareceHtml =
      trimmed.startsWith("<!DOCTYPE") ||
      trimmed.startsWith("<!doctype") ||
      trimmed.startsWith("<html");
    if (pareceHtml || res.status === 404) {
      throw new Error(
        `API /pagos-no-proveedor no disponible (HTTP ${res.status}). Falta implementar el endpoint en backend.`
      );
    }
    throw new Error(
      `${fallbackError}: respuesta no JSON del servidor (HTTP ${res.status})`
    );
  }
};

export const getEmpresas = async () => {
  const res = await fetch(`${UrlApi}/empresas`);
  const data = await parseJsonResponse(
    res,
    "No se pudieron obtener las empresas"
  );

  if (!res.ok || data?.ok === false) {
    throw new Error(
      errorFromBody(data, "No se pudieron obtener las empresas")
    );
  }

  return Array.isArray(data) ? data : data?.empresas ?? [];
};

/**
 * GET /pagos-no-proveedor
 */
export const getPagosNoProveedor = async ({
  id_empresa,
  concepto,
  estado,
  fecha_desde,
  fecha_hasta,
} = {}) => {
  const params = new URLSearchParams();
  if (id_empresa != null && id_empresa !== "") {
    params.set("id_empresa", String(id_empresa));
  }
  if (concepto) params.set("concepto", String(concepto));
  if (estado) params.set("estado", String(estado));
  if (fecha_desde) params.set("fecha_desde", String(fecha_desde));
  if (fecha_hasta) params.set("fecha_hasta", String(fecha_hasta));

  const qs = params.toString();
  const res = await fetch(`${BASE}${qs ? `?${qs}` : ""}`);
  const data = await parseJsonResponse(
    res,
    "No se pudieron obtener los pagos"
  );

  if (!res.ok || data?.ok === false) {
    throw new Error(
      errorFromBody(data, "No se pudieron obtener los pagos")
    );
  }

  return data;
};

/**
 * POST /pagos-no-proveedor
 */
export const crearPagoNoProveedor = async (body) => {
  const res = await fetch(BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await parseJsonResponse(res, "No se pudo crear el pago");

  if (!res.ok || data?.ok === false) {
    throw new Error(errorFromBody(data, "No se pudo crear el pago"));
  }

  return data;
};

/**
 * PUT /pagos-no-proveedor/:id
 */
export const actualizarPagoNoProveedor = async (id, body) => {
  const res = await fetch(`${BASE}/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await parseJsonResponse(res, "No se pudo actualizar el pago");

  if (!res.ok || data?.ok === false) {
    throw new Error(errorFromBody(data, "No se pudo actualizar el pago"));
  }

  return data;
};

/**
 * POST /pagos-no-proveedor/:id/pagar
 */
export const pagarPagoNoProveedor = async (id, body = {}) => {
  const res = await fetch(`${BASE}/${id}/pagar`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await parseJsonResponse(
    res,
    "No se pudo marcar como pagado"
  );

  if (!res.ok || data?.ok === false) {
    throw new Error(errorFromBody(data, "No se pudo marcar como pagado"));
  }

  return data;
};

/**
 * POST /pagos-no-proveedor/:id/anular
 */
export const anularPagoNoProveedor = async (id, body = {}) => {
  const res = await fetch(`${BASE}/${id}/anular`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await parseJsonResponse(res, "No se pudo anular el pago");

  if (!res.ok || data?.ok === false) {
    throw new Error(errorFromBody(data, "No se pudo anular el pago"));
  }

  return data;
};
